import { ref, watch, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useWikiPagesStore, type WikiPage, type WikiPageDetail } from '../stores/wiki_pages'

/**
 * Owns the "which page is selected" state for HomeView and the logic
 * that mutates it: deep-link resolution, content fetching, mobile
 * sidebar collapse, and the cross-tab refresh that fires when an
 * ActionCable broadcast updates the page list.
 *
 * The host component still owns the catalog (`pages`) since it shares
 * that data with the nav rail and WikiSidebar; we just receive it as a
 * Ref so the deep-link logic can rerun when the list arrives or
 * changes.
 *
 *   const { selectedId, selectedDetail, ... } = usePageSelection(pages)
 *   const loadCatalog = async () => {
 *     await wikiPages.fetchAll()
 *     selectDeepLinkedPage()
 *   }
 *
 * Mounting order: call this AFTER the catalog ref is bound (i.e. after
 * `storeToRefs(wikiPages)`) but BEFORE any computed that reads
 * `selectedDetail` (the document-title hook).
 */
export function usePageSelection(pages: Ref<WikiPage[]>) {
  const route = useRoute()
  const router = useRouter()
  const wikiPages = useWikiPagesStore()

  const selectedId = ref<number | null>(null)
  const selectedDetail = ref<WikiPageDetail | null>(null)
  const loadingContent = ref(false)
  // Monotonic ticket so out-of-order `fetchContent` responses can't
  // overwrite a newer selection. Without this, two rapid sidebar clicks
  // could land in either order — the second click sets selectedId, then
  // the first click's slow response arrives last and clobbers it.
  let selectionTicket = 0
  // Set when a /p/<path> deep link resolves to a path that isn't in
  // the WikiPage catalog — host renders a "page not found" state
  // instead of the default "select from sidebar" placeholder.
  const deepLinkMissing = ref<string | null>(null)
  const welcomeVisible = ref(false)
  // On narrow viewports the sidebar stacks above the detail pane and
  // the two are mutually exclusive. Default to collapsed so the main
  // pane (article, HomeLanding cards, or the not-found block) is the
  // primary view; the user opens the sidebar explicitly via the
  // meta-bar Back button or HomeLanding's "Browse catalog" button.
  // The collapse-on-navigation policy lives in its own watcher
  // below — selectPage / selectDeepLinkedPage no longer touch this.
  const sidebarHiddenMobile = ref(true)

  async function selectPage(id: number): Promise<void> {
    const ticket = ++selectionTicket
    selectedId.value = id
    selectedDetail.value = null
    welcomeVisible.value = false
    loadingContent.value = true
    try {
      const detail = await wikiPages.fetchContent(id)
      // A newer selectPage call has won — drop our (stale) response.
      if (ticket !== selectionTicket) return
      selectedDetail.value = detail
    } finally {
      if (ticket === selectionTicket) loadingContent.value = false
    }
  }

  // URL is the single source of truth for "what page is shown".
  // Sidebar clicks + search-result clicks navigate via router.push so
  // the back button works AND the header Log button doesn't silently
  // no-op when the URL is stuck on a page the sidebar has navigated
  // off.
  function navigateToPageById(id: number): void {
    const page = pages.value.find((p) => p.id === id)
    if (page) router.push(`/p/${page.path}`)
  }

  function navigateToPageByPath(path: string): void {
    if (pages.value.some((p) => p.path === path)) {
      router.push(`/p/${path}`)
    }
  }

  function showSidebarOnMobile(): void {
    sidebarHiddenMobile.value = false
  }

  // Re-hide the mobile sidebar without selecting a page — backs
  // the catalog's ✕ so "Browse catalog" then "never mind" returns to the
  // reading pane instead of stranding the user on the catalog.
  function hideSidebarOnMobile(): void {
    sidebarHiddenMobile.value = true
  }

  function clearSelection(): void {
    selectedId.value = null
    selectedDetail.value = null
  }

  // Resolve the URL's `/p/<path>` parameter to a WikiPage and auto-
  // select it. Missing pages surface an explicit "not found" state.
  function selectDeepLinkedPage(): void {
    const raw = route.params.path
    const path = Array.isArray(raw) ? raw.join('/') : raw

    if (!path) {
      // Root route — always render the host's landing branch
      // (HomeLanding's recent-pages cards + category chips). The
      // empty-catalog welcome block only kicks in when there are no
      // pages AT ALL — that's the "fresh install" case the cards UI
      // can't fill. `wiki/index.md` (if present) appears as a normal
      // card in HomeLanding's "Recently updated" grid and is browsable
      // via the sidebar's General group + the `/c/general` chip; we no
      // longer auto-route to it because it preempts HomeLanding in
      // any wiki that happens to have an index.md.
      deepLinkMissing.value = null
      clearSelection()
      welcomeVisible.value = pages.value.length === 0
      return
    }

    const match = pages.value.find((p) => p.path === path)
    if (match) {
      deepLinkMissing.value = null
      welcomeVisible.value = false
      selectPage(match.id)
    } else {
      deepLinkMissing.value = path
      clearSelection()
      welcomeVisible.value = false
    }
  }

  // Live-refresh the rendered article when an ActionCable broadcast
  // updates its row. Don't go through `selectPage` — that would auto-
  // collapse the sidebar on mobile, which is wrong for an in-place
  // update.
  watch(
    pages,
    async (list) => {
      if (selectedId.value == null) return
      const current = list.find((p) => p.id === selectedId.value)
      if (!current) {
        clearSelection()
        return
      }
      if (selectedDetail.value && current.updated_at !== selectedDetail.value.updated_at) {
        selectedDetail.value = await wikiPages.fetchContent(selectedId.value)
      }
    },
    { deep: true },
  )

  // Re-select when the URL changes while the view is already mounted
  // (e.g. navigating between two different /p/<path> URLs).
  watch(
    () => route.params.path,
    () => {
      selectDeepLinkedPage()
    },
  )

  // Mobile sidebar policy: collapse on every navigation so the main
  // pane (article, HomeLanding, not-found) wins. The user re-opens
  // the sidebar explicitly via the meta-bar Back button or
  // HomeLanding's "Browse catalog" button. Watching `route.path`
  // (not `route.params.path`) catches both `/p/...` → `/p/...` page
  // hops AND `/p/...` ↔ `/` transitions in one rule.
  watch(
    () => route.path,
    () => {
      sidebarHiddenMobile.value = true
    },
  )

  return {
    selectedId,
    selectedDetail,
    loadingContent,
    deepLinkMissing,
    welcomeVisible,
    sidebarHiddenMobile,
    selectPage,
    selectDeepLinkedPage,
    navigateToPageById,
    navigateToPageByPath,
    showSidebarOnMobile,
    hideSidebarOnMobile,
    clearSelection,
  }
}
